import { get, isArray } from 'lodash';

import { MobizHttpApiErrorResponse } from '../data/mobiz-http-api-error-response.do';
import { MobizHttpErrorResponse } from '../data/mobiz-http-error-response.do';

import { getStatusText, HttpStatusCode } from './http-status-code';
import { isOAuthApiEndpoint } from './mobiz-server-api.enum';

/**
 * Provides code for parsing HTTP error responses.
 *
 * This is a helper class for IMobizHttpService instances.
 */
export class MobizHttpErrorResponseHelper {

  public parseErrorResponseData(serverApi: string, statusCode: number, responseData: unknown): MobizHttpErrorResponse {
    let infoContent: Record<string, unknown> = this.getInfoContent(statusCode, responseData);
    // Handle all possible scenarios of OAuth error responses.
    // TODO: Update the backend to always include a response body containing a predefined error object.
    if (isOAuthApiEndpoint(serverApi) || this.isOAuthError(statusCode, infoContent)) {
      if (infoContent.hasOwnProperty('errorType')) {
        infoContent.originalErrorType = infoContent.errorType;
      }
      (infoContent as Partial<MobizHttpApiErrorResponse>).errorType = 'OAuth';
    }
    return new MobizHttpErrorResponse({
      status: statusCode,
      statusText: getStatusText(statusCode),
      info: infoContent,
    });
  }

  private isOAuthError(statusCode: number, content: unknown): boolean {
    // TODO: Refacor/remove this method once the OAuth errors always return a proper response body.
    if (statusCode === HttpStatusCode.UNAUTHORIZED) {
      return true;
    }
    if (statusCode === HttpStatusCode.BAD_REQUEST) {
      return get(content, 'error') === 'invalid_grant'
        || get(content, 'error') === 'invalid_clientId';
    }
    return false;
  }

  /**
   * This method processes the various possible contents of error responses.
   */
  private getInfoContent(statusCode: number, responseData: unknown): Record<string, unknown> {
    let content: Record<string, unknown> = {};
    if (typeof responseData === 'string') {
      content = this.processStringResponseData(responseData);
    } else if (typeof responseData === 'object' && responseData !== null) {
      content = this.processObjectResponseData(responseData as Record<string, unknown>);
    }
    if (Object.keys(content).length === 0) {
      /**
       * The default StatusCodeResult responses don't support reason phrases and are empty.
       *
       * @see mobiz-backend:Mobiz.Web.Core.Helpers.MobizStatusCodeResult
       * @see mobiz-backend:Microsoft.AspNetCore.Mvc.StatusCodeResult
       * @see mobiz-backend:Microsoft.AspNetCore.Mvc.ControllerBase
       */
      content = { error: getStatusText(statusCode) };
    }
    if (!content.hasOwnProperty('errorType')) {
      (content as Partial<MobizHttpApiErrorResponse>).errorType = 'Uncategorized';
    }
    return content;
  }

  private processObjectResponseData(responseData: Record<string, unknown>): Record<string, unknown> {
    try {
      // TODO: TP-8126 Remove this code when the API no longer returns this empty key.
      if (Object.keys(responseData).length === 1 && responseData.hasOwnProperty('')) {
        if (isArray(responseData[''])) {
          return { errors: responseData[''] };
        } else {
          return { error: responseData[''] };
        }
      } else {
        return responseData;
      }
    } catch (err) {
      return responseData;
    }
  }

  private processStringResponseData(responseData: string): Record<string, unknown> {
    try {
      let content = JSON.parse(responseData);
      if (isArray(content)) {
        return { errors: content };
      } else {
        return content;
      }
    } catch (err) {
      // Map string errors to the error key.
      return { error: responseData };
    }
  }
}
